// The chart model: what the chart draws, from what the API returned. Pure,
// DOM-free, so it can be unit-tested. main.ts drawChart does the fetching and
// hands the frames in, then drives uPlot and the legend from the result.
//
// The steps, in order: group the base frame into lines (lib/lines), filter each
// overlay to its series, take the union of dates as the shared axis, transform
// each line on its own dates and frequency (lib/transforms transformLine),
// assign colour, dash and label, and name the selected combinations that drew
// nothing.
import { label, lineDims, type Meta, type Selection } from "./meta";
import { channelsFor, groupLines, lineLabel, missingTuples, varyingOf, type LongFrame } from "./lines";
import { periodMidShiftSec, transformLine } from "./transforms";

// "Warm Swiss" series palette (design_handoff_color_system §3): lead teal,
// second vermilion, then a hue+lightness-spread ramp (no red/coral/pink so the
// second-red stays unambiguous; color-blind robust).
export const SERIES_COLORS = [
  "#2E6B76",  // 1 teal   (lead / main)
  "#E04A2F",  // 2 red    (second / signal)
  "#3A6FB0",  // 3 blue
  "#DDA02E",  // 4 amber
  "#4F9559",  // 5 green
  "#8A57A4",  // 6 purple
  "#9E6A3C",  // 7 brown
  "#5F7E92",  // 8 slate
  "#87883C",  // 9 olive
  "#B58A2E","#4E8F9A","#6F5FA0","#8A7A4A"  // 10–13 overflow
];
// Dash patterns for the 2nd varying line dim (solid first). uPlot takes a
// canvas setLineDash array per series; SVG/legend reuse the same arrays. Only a
// handful read as distinct, so the dash channel is best with few levels.
export const DASH_PATTERNS: number[][] = [[], [7, 4], [2, 3], [9, 4, 2, 4], [12, 6], [1, 4]];

const color = (i: number): string => SERIES_COLORS[i % SERIES_COLORS.length] as string;

/** Display label of a level, falling back to its code. */
export function levelLabel(meta: Meta, dim: string, code: string): string {
  const lv = meta.dimensions?.[dim]?.levels?.[code];
  return (lv && label(lv.label)) || code;
}

/** One overlay (a series from another dataset) as fetched. `ref` is passed
 * through to the line, so the legend can offer to remove it. */
export interface Overlay<R> {
  label: string;
  /** Second legend part, the overlay's dataset title for a sub-series. */
  sub: string | null;
  /** The overlay's series within its frame; without both, the whole frame. */
  dim?: string | null;
  code?: string | null;
  frame: LongFrame;
  /** The overlay's own frequency; the base dataset's when unknown. */
  freq?: string | null;
  /** Its changes as differences (a series in percent), see TransformOpts.diff. */
  diff?: number;
  ref: R;
}

/** The visible window the index view rebases on, in display (shifted) seconds. */
export interface IndexWindow {
  /** Left edge of a live plot's x-scale, null when there is no plot. */
  scaleMin: number | null;
  /** Left edge of a window still to be applied (a shared URL's r=). */
  pendingMin: number | null;
  /** "all", a preset in years ("1", "5", "10") or "custom". */
  range: string | null | undefined;
}

export interface ChartLine<R> {
  label: string;
  color: string;
  dash: number[];
  values: (number | null)[];
  sub?: string | null;
  extraRef?: R;
}

export interface ChartModel<R> {
  /** The shared axis: every line's dates, sorted (raw period starts). */
  dates: string[];
  /** The axis in display seconds, at the period midpoint (`shift` added). */
  x: number[];
  shift: number;
  /** Base lines first, then overlays. */
  lines: ChartLine<R>[];
  /** How many of `lines` are base lines. */
  nBase: number;
  /** Labels of the selected combinations that drew no line. */
  missing: string[];
}

export interface ChartInput<R> {
  meta: Meta;
  selection: Selection;
  /** The base dataset's /series frame for the selection. */
  base: LongFrame;
  overlays: Overlay<R>[];
  transform: string;
  window: IndexWindow;
  /** Chart title: the single-series label and the label when nothing varies. */
  title: string;
  /** The base series are in percent: changes as differences (TransformOpts.diff). */
  diff?: number;
}

const isPreset = (r: string | null | undefined): r is string => !!r && /^\d+$/.test(r);

// The RAW (unshifted) epoch seconds of the visible window's left edge, which
// the index view rebases on: a live drag-zoom, a pending URL window or a range
// preset, else the start of the data. `xs` are the raw axis instants.
function indexWindowMinRaw(xs: number[], shift: number, w: IndexWindow): number {
  const fullMin = xs[0] as number, fullMax = xs[xs.length - 1] as number;
  if (w.scaleMin != null) {
    const m = w.scaleMin - shift;
    if (m > fullMin + 1) return m;
  }
  if (w.pendingMin != null) return w.pendingMin - shift;
  if (isPreset(w.range)) return Math.max(fullMin, fullMax - parseInt(w.range, 10) * 365.25 * 86400);
  return fullMin;
}

/** Everything the chart draws. null when there is no line to draw. */
export function buildChartModel<R>(o: ChartInput<R>): ChartModel<R> | null {
  const { meta, selection } = o;
  const freq = meta.frequency as string | null | undefined;
  const lds = lineDims(meta);
  const levelOf = (d: string, code: string) => levelLabel(meta, d, code);

  // Base: one line per distinct tuple of the line dims present in the frame
  // (the cross-product of the selected levels); empty cells just don't appear.
  // A dataset without line dims is one line.
  type Raw = { tuple?: Record<string, string>; label?: string; sub?: string | null;
    dateMap: Map<string, number | null>; freq?: string | null; diff?: number; ref?: R; overlay?: true };
  const raw: Raw[] = [];
  const base = o.base;
  if (lds.some(d => base[d])) {
    for (const g of groupLines(lds, selection, base)) {
      const m = new Map<string, number | null>();
      for (let i = 0; i < g.dates.length; i++) m.set(g.dates[i] as string, g.values[i] ?? null);
      raw.push({ tuple: g.tuple, dateMap: m });
    }
  } else {
    const m = new Map<string, number | null>();
    for (let r = 0; r < (base.date || []).length; r++) m.set(base.date[r] as string, base.value[r] ?? null);
    raw.push({ label: o.title, dateMap: m });
  }
  // Overlays: the one series each stands for; an overlay with no data is left out.
  for (const ov of o.overlays) {
    const f = ov.frame, n = (f.date || []).length, dm = new Map<string, number | null>();
    const col = ov.dim && ov.code && f[ov.dim] ? f[ov.dim] : null;
    for (let r = 0; r < n; r++) if (!col || col[r] === ov.code) dm.set(f.date[r] as string, f.value[r] ?? null);
    if (dm.size) raw.push({ label: ov.label, sub: ov.sub, dateMap: dm, freq: ov.freq, diff: ov.diff, ref: ov.ref, overlay: true });
  }
  if (!raw.length) return null;

  const dateSet = new Set<string>();
  raw.forEach(s => s.dateMap.forEach((_, d) => dateSet.add(d)));
  const dates = [...dateSet].sort();
  const xs = dates.map(d => Date.parse(d) / 1000);   // raw period-start instants
  const shift = periodMidShiftSec(freq) || 0;        // plotted at the period midpoint
  // Index view: rebase at the first date in the visible window.
  let windowStart: string | null = null;
  if (o.transform === "index" && dates.length) {
    const wmin = indexWindowMinRaw(xs, shift, o.window);
    let s = 0; while (s < xs.length && (xs[s] as number) < wmin - 1) s++;
    windowStart = dates[s < xs.length ? s : 0] as string;
  }
  // Each line is transformed on its own dates and frequency, then placed on the
  // shared axis.
  const values = (s: Raw) => transformLine(dates, s.dateMap, {
    transform: o.transform, freq: s.freq || freq, windowStart,
    diff: s.overlay ? s.diff : o.diff,
  });

  // Channels over the base (cross-product) lines: 1st varying dim -> colour, 2nd
  // -> dash, 3+ -> per-tuple colour. Overlays continue the palette after them.
  const tupleLines = raw.filter((s): s is Raw & { tuple: Record<string, string> } => !!s.tuple);
  const varyDims = varyingOf(lds, tupleLines);
  let baseColors = 1;
  const baseLines: ChartLine<R>[] = raw.filter(s => !s.overlay).map((s, i) => {
    if (!s.tuple) return { label: s.label || o.title, color: color(0), dash: [], values: values(s) };
    const ch = channelsFor(s.tuple, i, varyDims, selection);
    baseColors = Math.max(baseColors, ch.colorIdx + 1);
    return {
      label: lineLabel(s.tuple, varyDims, levelOf, o.title),
      color: color(ch.colorIdx),
      dash: DASH_PATTERNS[ch.dashIdx % DASH_PATTERNS.length] as number[],
      values: values(s),
    };
  });
  const overlayLines: ChartLine<R>[] = raw.filter(s => s.overlay).map((s, j) => ({
    label: s.label as string, sub: s.sub, extraRef: s.ref,
    color: color(baseColors + j), dash: [], values: values(s),
  }));

  // Selected combinations with no data draw nothing; name them, or a picked
  // series just fails to appear. Labelled by the dims the selection varies.
  const selVarying = lds.filter(d => (selection[d] || []).length > 1);
  const missing = tupleLines.length
    ? missingTuples(lds, selection, tupleLines).map(tp => lineLabel(tp, selVarying, levelOf, o.title))
    : [];

  return {
    dates, x: shift ? xs.map(t => t + shift) : xs, shift,
    lines: [...baseLines, ...overlayLines], nBase: baseLines.length, missing,
  };
}
